import React, { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";

// @material-ui/core components
import {
  createJSONProjectAPI,
  getJSONProjectsAPI,
  getJSONProjectAPI,
  cloneJSONProjectAPI,
  deleteJSONProjectAPI,
  inviteUserAPI,
} from "views/APIs/APIUtility";

// @material-ui/icons
// import ContentCopy from "@material-ui/icons/ContentCopy";
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
} from "@material-ui/core";

// core components

import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context.js";
import { CloudUpload, Create, DoneAll } from "@material-ui/icons";
import CreateCourseCard from "views/Components/CreateCourseCard";
import AssetDashboard from "views/Components/AssetDashboard";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import CreateFormModal from "views/Components/CreateFormModal";
import { fetchImageAsFile } from "views/Dashboard/Course/CourseConstants";
import FormDetails from "../Models/FormDetails";

const useStyles = makeStyles({
  ...styles,
});

export default function FormsDesign() {
  const asset_identifier = "form";
  const images = require.context("assets/img/create_cards_16_9/", true);

  // state variables
  const { path, url } = useRouteMatch();
  const classes = useStyles();
  const [createFormModal, setCreateFormModal] = React.useState(false);
  const [newFormDetails, setNewFormDetails] = React.useState(new FormDetails());
  const [iconImage, setIconImage] = React.useState();
  const [allForms, setAllForms] = React.useState([]);
  const [initialForms, setInitialForms] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });

  const [FormAdded, setFormAdded] = React.useState(false);
  const { showNotification } = React.useContext(NotificationContext);

  const handleDeleteForm = async (data) => {
    const formData = new FormData();
    formData.append("json_project_id", data._id);

    try {
      await deleteJSONProjectAPI(formData);
      setAllForms(allForms.filter((prevForm) => prevForm._id !== data._id));
      setInitialForms(
        initialForms.filter((prevForm) => prevForm._id !== data._id)
      );
      showNotification("Form deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleCloneForm = async (data) => {
    try {
      setLoading({ loading: true, message: "Cloning Form..." });
      const formData = new FormData();
      formData.append("json_project_id", data._id);
      const data = await cloneJSONProjectAPI(formData);
      setAllForms((prevForms) => [...prevForms, _mapResourceToAppType(data)]);
      setFormAdded(true);
      showNotification("Form cloned successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const requestData = {
        collection_type: "json_form_design",
        project_id: data._id,
        invitee_email: data.email,
      };

      const response = await inviteUserAPI(requestData);

      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allForms.find(
        (asset) => asset._id === updatedProjectId
      );
      if (!updatedAsset) {
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };

      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" } // Update status if email matches
          : invitee
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allCourses state
      const updatedForms = allForms.map((Form) =>
        Form._id === updatedProjectId ? newAsset : Form
      );

      setAllForms(updatedForms);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Fetch all Forms
      try {
        setLoading({ loading: true, message: "Loading Forms..." });
        const data = await getJSONProjectsAPI();
        const mappedData = data.map(_mapResourceToAppType);
        setAllForms(mappedData);
        setInitialForms(mappedData);
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchData();
  }, []);

  const handleCreateForm = async () => {
    setLoading({ loading: true, message: "Creating Form..." });
    const formData = new FormData();
    formData.append("name", newFormDetails.name);
    formData.append("description", newFormDetails.description);
    newFormDetails.uploadedFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const data = await createJSONProjectAPI(formData);
      window.open(`/create/form-design/${data._id}`, "_blank");

      setAllForms([...allForms, _mapResourceToAppType(data)]);
      setFormAdded(true);
      showNotification("Form created successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      handleCreateFormModalClose();
      setLoading({ loading: false, message: "" });
    }
  };

  const clearCreateModal = () => {
    setNewFormDetails(new FormDetails());
    setIconImage(null);
  };

  const handleCreateFormModalClose = () => {
    setCreateFormModal(false);
    // setModal(false);
    clearCreateModal();
  };

  const handleFileUpload = (files) => {
    setNewFormDetails({ ...newFormDetails, uploadedFiles: files });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "In Design Phase":
        return <Create style={{ color: "#435966" }} />;
      case "Ready for Publishing":
        return <CloudUpload style={{ color: "#435966" }} />;
      case "Published":
        return <DoneAll style={{ color: "#435966" }} />;
      default:
        return <Create style={{ color: "#435966" }} />;
    }
  };

  const _mapResourceToAppType = (resource) => {
    // Destructure the resource to exclude course_name and course_description
    const { name, description, ...rest } = resource;

    return {
      ...rest, // Spread all remaining properties
      name: name, // Add name property
      description: description, // Add description property
    };
  };

  //   const handleSaveFormTags = async (FormData) => {
  //     try {
  //       const formData = new FormData();
  //       formData.append("Form_id", FormData._id);
  //       if (FormData.tags.length === 0) {
  //         formData.append("tags", []);
  //       }
  //       // Append each tag separately
  //       else {
  //         FormData.tags.forEach(tag => formData.append("tags", tag));
  //       }
  //       await updateFormTagsAPI(formData);
  //       setAllForms((prevForms) =>
  //         prevForms.map((Form) =>
  //           Form._id === FormData._id ? { ...Form, tags: FormData.tags } : Form
  //         )
  //       );
  //     }
  //     catch (error) {
  //       showNotification(error, "danger");
  //     }
  //     finally { }
  //   };

  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <CreateFormModal
        newFormDetails={newFormDetails}
        setNewFormDetails={setNewFormDetails}
        createFormModal={createFormModal}
        handleCreateFormModalClose={handleCreateFormModalClose}
        handleCreateForm={handleCreateForm}
      />

      <AssetDashboard
        path={path}
        welcomeTitle="Form Generator Dashboard"
        welcomeSubtitle="Start by creating a new Form or reviewing your existing Forms."
        allAssets={allForms}
        setAllAssets={setAllForms}
        initialAssets={initialForms}
        noAssetMessage="Create or Review your Forms to get started."
        linkPath="/"
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreateFormModal}
        createAssetButtonText="Create Checklist"
        // handleSaveFormTags={handleSaveFormTags}
        handleInviteSend={handleInviteSend}
        asset_identifier={asset_identifier}
        handleDeleteAsset={handleDeleteForm}
        handleCloneAsset={handleCloneForm}
        assetAdded={FormAdded}
        setAssetAdded={setFormAdded}
        setInitialAssets={setInitialForms}
        workflowConfig={{
          type: "form",
          title: "Interactive Form Generation Workflow",
          description: `Transform documents and structured plans into interactive forms or forms for collecting responses.`,
          steps: [
            {
              label: "1. Outline project details",
              body:
                "Enter the project details and upload the document or plan you want to convert into a form.",
            },
            {
              label: "2. Extract & validate content",
              body:
                "Check the extracted content for accuracy and confirm it before generating the interactive form.",
            },
            {
              label: "3. Preview the form",
              body:
                "Inspect the generated JSON definition of the form, validate its structure, and preview the form layout.",
            },
            {
              label: "4. Publish & monitor responses",
              body:
                "Publish the interactive form, share the link with your audience, and monitor responses or entries in real time.",
            },
          ],
          videoUrl:
            "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/form-design-demo.mp4",
          examples: [
            {
              title: "Credit Risk Model",
              description: "Form for credit risk model",
              link:
                "https://qucreate.qusandbox.com/create/form-design/68b84fa965c321888c8875b1?stage=4",
              mediaUrl:
                "https://powerslides.com/wp-content/uploads/2021/11/Credit-Risk-Modelling-4.png",
              mediaAlt: "Credit Risk Model",
            },
            {
              title: "BNPL Checklist",
              description:
                "Due diligence checklist for the model development plan for the BNPL Credit Risk Model",
              link:
                "https://qucreate.qusandbox.com/create/form-design/68c970f9449867d7d7008b6e?stage=4",
              mediaUrl:
                "https://seon.io/wp-content/uploads/2023/06/BNPL_2-1.svg",
              mediaAlt: "BNPL Checklist",
            },
          ],
        }}
      />
    </div>
  );
}
